import React, { useContext, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Collapse, TextTitle, TitleBarError, useCollapseState } from '@bingads-webui-campaign/wizard-jumbo-card';

import LocationIcon from '../media/location.svg';

import { TargetingLocation } from './targeting-location';
import { getLocationTargetTitleDetails } from '../utils/common';
import { useTargetInstrument } from '../use-target-instrument';
import { validateLocations } from '../utils/validation';

export const CollapsibleLocation = ({
  data,
  onDataChange,
  isLoading,
}) => {
  const i18n = useContext(I18nContext);
  const { locationLogger } = useTargetInstrument();

  const { targets, quickOptionCountry } = data || {};
  const {
    negativeLocationTargets = [],
    positiveLocationTargets = [],
    selectedLocationOption,
  } = targets || {};

  const [
    allSections,
    expandedSections,
    setExpandedSections,
  ] = useCollapseState(['locations'], ['locations']);

  const locationDetails = useMemo(() => getLocationTargetTitleDetails(
    positiveLocationTargets,
    negativeLocationTargets,
    selectedLocationOption,
    i18n
  ), [i18n, negativeLocationTargets, positiveLocationTargets, selectedLocationOption]);

  const onLocationChange = useCallback((newTargets) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        targets: {
          ...prevData.targets,
          ...newTargets,
        },
      }),
    });
  }, [onDataChange]);

  const locationsErrorString = useMemo(() => {
    const errorString = validateLocations(targets);
    return errorString ? i18n.getString(errorString) : '';
  }, [i18n, targets]);

  // Update error string into state machine context
  useEffect(() => {
    onLocationChange({ locationError: locationsErrorString });
  }, [locationsErrorString, onLocationChange]);

  return (
    <Collapse allPanelItems={allSections} checkItems activeKeys={expandedSections} setActiveKeys={setExpandedSections}>
      <Collapse.Panel
        key="locations"
        title={<TextTitle
          titleKey={_TL_('Location')}
          icon={LocationIcon}
          details={locationDetails}
          topicKey="pop_BA_Settings_LocationTargeting"
        />}
        error={!isLoading && <TitleBarError error={locationsErrorString} />}
      >
        <TargetingLocation
          data={targets}
          onDataChange={onLocationChange}
          quickOption={quickOptionCountry}
          logger={locationLogger}
          isLoading={isLoading}
        />
      </Collapse.Panel>
    </Collapse>
  );
};

CollapsibleLocation.propTypes = {
  data: PropTypes.shape({}).isRequired,
  onDataChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

CollapsibleLocation.defaultProps = {
  isLoading: false,
};
